import { subscribeToGlobalEvent } from "@storytell/events";
import type { AssetId, ConceptId, LabelId } from "@storytell/types";
import { createEffect, createSignal, on, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";

declare const RUNNING_ENV: string;
declare const STORYTELL_API_URL: string;

export interface Suggestion {
  id: string;
  text: string;
  category?: string;
  persona?: string;
  scopeType: "asset" | "label" | "concept";
  scopeId: string;
}

const isSuggestion = (value: unknown): value is Suggestion =>
  typeof value === "object" &&
  value !== null &&
  "id" in value &&
  typeof value.id === "string" &&
  "text" in value &&
  typeof value.text === "string" &&
  "scopeType" in value &&
  (value.scopeType === "asset" || value.scopeType === "label" || value.scopeType === "concept") &&
  "scopeId" in value &&
  typeof value.scopeId === "string";

const suggestionsOf = (body: unknown): Suggestion[] =>
  typeof body === "object" && body !== null && "suggestions" in body && Array.isArray(body.suggestions)
    ? body.suggestions.filter(isSuggestion)
    : [];

/** Scope for fetching suggestions for specific entities (used to merge into cache for detail pages). */
interface SuggestionsScope {
  projectId: string;
  assetIds?: string[];
  labelIds?: string[];
  conceptIds?: string[];
}

function scopeKey(scope: SuggestionsScope): string {
  const a = (scope.assetIds ?? []).toSorted().join(",");
  const l = (scope.labelIds ?? []).toSorted().join(",");
  const c = (scope.conceptIds ?? []).toSorted().join(",");
  return `${scope.projectId}:${a}:${l}:${c}`;
}

const getBackendUrl = (): string => {
  if (typeof window === "undefined") return "http://localhost:3443";
  if (RUNNING_ENV === "local") {
    const override = typeof STORYTELL_API_URL === "undefined" ? "" : STORYTELL_API_URL;
    return override || "http://localhost:3443";
  }
  if (RUNNING_ENV === "development") return "https://api.storytell-development.ai";
  return "https://api.storytell.ai";
};

export type SuggestionsService = ReturnType<typeof createSuggestionsService>;

export const createSuggestionsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  projectId: () => string | undefined;
}) => {
  const [allSuggestions, setAllSuggestions] = createSignal<Suggestion[]>([]);
  const [isLoading, setIsLoading] = createSignal(false);
  const [hasFetched, setHasFetched] = createSignal(false);

  // Track last fetched project to avoid duplicate fetches
  let lastFetchedProjectId: string | null = null;
  let inFlightFetch: Promise<Suggestion[]> | null = null;

  // Track fetched scopes to avoid refetching when effect re-runs (multiple scopes can be fetched)
  const fetchedScopeKeys = new Set<string>();
  const inFlightScopes = new Map<string, Promise<void>>();

  const fetchAllSuggestions = async (projectId: string): Promise<Suggestion[]> => {
    const token = deps.authToken();
    if (!token) return [];

    // Project scope rides on X-Project-Id — the backend handler resolves
    // tenancy from the header, not from a query param.
    const url = `${getBackendUrl()}/suggestions`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Project-Id": projectId,
      },
    });

    if (!response.ok) return [];
    const data: unknown = await response.json();
    return suggestionsOf(data);
  };

  const fetchByScope = async (scope: SuggestionsScope): Promise<Suggestion[]> => {
    const token = deps.authToken();
    if (!token) return [];

    const params = new URLSearchParams();
    if (scope.assetIds?.length) params.set("assetIds", scope.assetIds.join(","));
    if (scope.labelIds?.length) params.set("labelIds", scope.labelIds.join(","));
    if (scope.conceptIds?.length) params.set("conceptIds", scope.conceptIds.join(","));

    const query = params.toString();
    const url = query ? `${getBackendUrl()}/suggestions?${query}` : `${getBackendUrl()}/suggestions`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Project-Id": scope.projectId,
      },
    });

    if (!response.ok) return [];
    const data: unknown = await response.json();
    return suggestionsOf(data);
  };

  const fetchAndMergeForScope = async (scope: SuggestionsScope): Promise<void> => {
    if (isServer) return;
    const hasScope =
      (scope.assetIds?.length ?? 0) > 0 || (scope.labelIds?.length ?? 0) > 0 || (scope.conceptIds?.length ?? 0) > 0;
    if (!hasScope) return;

    const token = deps.authToken();
    if (!token || !deps.authReady()) return;
    if (scope.projectId !== deps.projectId()) return;

    const key = scopeKey(scope);
    if (fetchedScopeKeys.has(key)) return;
    const existing = inFlightScopes.get(key);
    if (existing) return existing;

    const doFetch = async () => {
      try {
        const suggestions = await fetchByScope(scope);
        if (suggestions.length === 0) return;
        setAllSuggestions((prev) => {
          const byId = new Map(prev.map((s) => [s.id, s]));
          for (const s of suggestions) byId.set(s.id, s);
          return Array.from(byId.values());
        });
        fetchedScopeKeys.add(key);
      } catch {
        // Ignore merge errors (e.g. network); cache remains as-is
      } finally {
        inFlightScopes.delete(key);
      }
    };

    const promise = doFetch();
    inFlightScopes.set(key, promise);
    return promise;
  };

  /** Refetch suggestions for a scope and merge (bypasses cache). Use when opening a detail view to get the full count. */
  const refetchAndMergeForScope = async (scope: SuggestionsScope): Promise<void> => {
    const key = scopeKey(scope);
    fetchedScopeKeys.delete(key);
    inFlightScopes.delete(key);
    return fetchAndMergeForScope(scope);
  };

  const ensureSuggestions = async (): Promise<Suggestion[]> => {
    if (isServer) return [];

    const projectId = deps.projectId();
    if (!projectId || !deps.authReady()) return [];

    // Return cached suggestions if we already fetched for this project
    if (hasFetched() && projectId === lastFetchedProjectId) {
      return allSuggestions();
    }

    // If already fetching, wait for it
    if (inFlightFetch && projectId === lastFetchedProjectId) {
      return inFlightFetch;
    }

    // Start new fetch
    setIsLoading(true);
    lastFetchedProjectId = projectId;

    try {
      inFlightFetch = fetchAllSuggestions(projectId);
      const suggestions = await inFlightFetch;
      setAllSuggestions(suggestions);
      setHasFetched(true);
      return suggestions;
    } finally {
      inFlightFetch = null;
      setIsLoading(false);
    }
  };

  const clearCache = () => {
    setAllSuggestions([]);
    setHasFetched(false);
    lastFetchedProjectId = null;
    fetchedScopeKeys.clear();
  };

  const refetch = () => {
    clearCache();
    ensureSuggestions().catch((error: unknown) => console.error("Failed to fetch suggestions:", error));
  };

  // Filter suggestions for a specific asset
  const getSuggestionsForAsset = (assetId: AssetId): Suggestion[] => {
    return allSuggestions().filter((s) => s.scopeType === "asset" && s.scopeId === assetId);
  };

  // Filter suggestions for a specific concept
  const getSuggestionsForConcept = (conceptId: ConceptId): Suggestion[] => {
    return allSuggestions().filter((s) => s.scopeType === "concept" && s.scopeId === conceptId);
  };

  // Filter suggestions for a specific label
  const getSuggestionsForLabel = (labelId: LabelId): Suggestion[] => {
    return allSuggestions().filter((s) => s.scopeType === "label" && s.scopeId === labelId);
  };

  // Auto-fetch when project changes
  createEffect(
    on(
      () => deps.projectId(),
      (projectId) => {
        if (isServer || !projectId || !deps.authReady()) return;
        // Clear cache when project changes so we refetch for the new project
        if (projectId !== lastFetchedProjectId) {
          refetch();
        }
      }
    )
  );

  // Subscribe to events that might update suggestions
  onMount(() => {
    const unsubscribeUserContext = subscribeToGlobalEvent("user-context-changed", () => {
      refetch();
    });
    const unsubscribeRefresh = subscribeToGlobalEvent("refresh-sync", () => {
      refetch();
    });
    const unsubscribeConnectivityRestored = subscribeToGlobalEvent("connectivity-restored", () => {
      refetch();
    });
    onCleanup(() => {
      unsubscribeUserContext();
      unsubscribeRefresh();
      unsubscribeConnectivityRestored();
    });
  });

  return {
    /** Whether suggestions are currently being fetched */
    isLoading,
    /** Whether suggestions have been fetched at least once for current project */
    hasFetched,
    /** Ensure suggestions are loaded, returns cached if available */
    ensureSuggestions,
    /** Get suggestions filtered for a specific asset */
    getSuggestionsForAsset,
    /** Get suggestions filtered for a specific concept */
    getSuggestionsForConcept,
    /** Get suggestions filtered for a specific label */
    getSuggestionsForLabel,
    /** Refetch scope and merge (bypass cache). Use for detail view to get full count. */
    refetchAndMergeForScope,
  };
};
